<script setup lang="ts">
import type inputFullPhone from '@/components/base/form/InputFullPhone.vue';

const { $containment, $kyc } = useNuxtApp();
const { t } = useTexts();
const { ApiService } = useApiService();
const { userPhone, userPhoneDdi, fetchUserProfile } = useUserData();
const { openToaster } = useLayoutStore();
const { openForgotPassword } = useAuth();
const { resetRecoveryPasswordFlow } = useAuthStore();
const { isAuthenticated, recoveryPasswordFlow } = storeToRefs(useAuthStore());
const { lastDateSmsSent, setLastDateSmsSent } = useUserValidations();
const { phoneToChange } = storeToRefs(useUserActivityStore());
const { resetPhoneToChange, setPhoneToChange } = useUserActivityStore();
const { closeModal } = useLayoutStore();
const { isValidationModuleEnabled } = useModulesValidation();

const SECONDS_TO_RESEND_CODE = 60;

const typedCode = ref('');
const isLoading = ref(false);

const isValid = computed<boolean>(() => {
  return typedCode.value.length === 5;
});

function onSubmit() {
  if (isChangeNumberFieldVisible.value && !phoneToChange.value) {
    onChangeNumberConfirmButtonClick();
  } else {
    onConfirmButtonClick();
  }
}

function handleError(err: any, fallbackMessageKey: string) {
  const errorMessage = err?.data?.message || t(fallbackMessageKey);

  if (err.status === 406) {
    closeModal();
  }

  openToaster({
    message: errorMessage,
    type: 'error',
  });
}

function handleSuccess(
  res: { status: number; data: { success: boolean; message: string } },
  successCallback?: (res: any) => void,
) {
  if (res.status.toString().startsWith('20') && res.data.success) {
    successCallback?.(res);
  } else {
    const message = res.data.message || t('auth.register.error.status_error');

    openToaster({
      message,
      type: 'error',
    });
  }
}

const secondsToResendCode = ref(SECONDS_TO_RESEND_CODE);
const countdownInterval = ref<number | null>(null);

function startCountdown() {
  if (countdownInterval.value) {
    clearInterval(countdownInterval.value);
  }

  const lastDate = lastDateSmsSent.value
    ? new Date(lastDateSmsSent.value).getTime()
    : 0;

  const now = new Date().getTime();
  const elapsedSeconds = Math.floor((now - lastDate) / 1000);
  const remainingSeconds = SECONDS_TO_RESEND_CODE - elapsedSeconds;

  secondsToResendCode.value = remainingSeconds > 0 ? remainingSeconds : 0;

  countdownInterval.value = setInterval(() => {
    if (secondsToResendCode.value > 0) {
      secondsToResendCode.value -= 1;
    } else {
      if (countdownInterval.value !== null) {
        clearInterval(countdownInterval.value);
      }
      countdownInterval.value = null;
      setLastDateSmsSent(null);
    }
  }, 1000) as unknown as number;
}

const canResendCode = computed<boolean>(() => {
  if (!lastDateSmsSent.value) return true;

  return secondsToResendCode.value <= 0;
});

interface PhoneToChange {
  phone?: string;
  ddi?: string;
  source?: string;
  kyc_id?: number;
  token?: string;
}

async function sendSmsCode() {
  if (!canResendCode.value) return;

  isLoading.value = true;
  typedCode.value = '';

  const payload: PhoneToChange = {};

  if (isAuthenticated.value) {
    if (phoneToChange.value) {
      payload.phone = phoneToChange.value.phone;
      payload.ddi = phoneToChange.value.ddi;
    }

    if ($kyc.current.source) {
      payload.source = $kyc.current.source;
    }

    if ($kyc?.current?.id) {
      payload.kyc_id = $kyc.current.id;
    }
  } else if (recoveryPasswordFlow.value?.token) {
    payload.token = recoveryPasswordFlow.value.token;
  }

  try {
    const endpoint = isAuthenticated.value
      ? '/bff/users/send-sms'
      : '/auth/passwords/reset/by-sms';

    const res = await ApiService.post(endpoint, payload);

    handleSuccess(res, () => {
      openToaster({
        message: t('verification.sms.resend_successful'),
        type: 'success',
      });
    });

    setLastDateSmsSent(new Date().toISOString());
    startCountdown(); // Inicia o countdown após enviar o código
  } catch (err: any) {
    handleError(err, t('auth.register.error.response_error'));

    if (err?.status === 403 && recoveryPasswordFlow.value?.token) {
      await solvePhoneContainment();
      resetRecoveryPasswordFlow();
      openForgotPassword();
    }
  } finally {
    isLoading.value = false;
  }
}

async function validateEmailCode() {
  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const payload: Record<string, any> = { code: typedCode.value };

  if (isAuthenticated.value) {
    if (phoneToChange.value) {
      const { phone, ddi } = phoneToChange.value;

      Object.assign(payload, { phone, ddi });
    }
  } else if (recoveryPasswordFlow.value?.token) {
    Object.assign(payload, {
      token: recoveryPasswordFlow.value.token,
      type: 'sms',
    });
  }

  try {
    const endpoint = isAuthenticated.value
      ? '/bff/users/validade-sms-code'
      : '/auth/passwords/reset/validate-code';

    const res = await ApiService.post(endpoint, payload);

    handleSuccess(res, async () => {
      setLastDateSmsSent(null);
      resetPhoneToChange();

      if (isAuthenticated.value) {
        await fetchUserProfile({ force: true });
      }

      const { setRecoveryPasswordData } = useAuthStore();

      setRecoveryPasswordData({
        code: typedCode.value,
      });

      await solvePhoneContainment();
    });
  } catch (err: any) {
    handleError(err, 'auth.register.error.response_error');

    if (err?.status === 403 && recoveryPasswordFlow.value?.token) {
      await solvePhoneContainment();
      resetRecoveryPasswordFlow();
      openForgotPassword();
    }
  } finally {
    isLoading.value = false;
  }
}

async function onResendCodeButtonClick() {
  await sendSmsCode();
}

async function onConfirmButtonClick() {
  await validateEmailCode();
}

async function start() {
  await sendSmsCode();
  startCountdown();
}

async function solvePhoneContainment() {
  await $containment.solve({
    name: 'phone',
    customValidator: () => true,
  });
}

const inputPhoneRef = ref<InstanceType<typeof inputFullPhone> | null>(null);

const selectedPhoneNumber = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneNumber ?? '';
});

const selectedPhoneDdi = computed<string>(() => {
  return inputPhoneRef.value?.selectedPhoneDdi ?? '';
});

const isChangeNumberFieldVisible = ref(false);

function onChangeNumberButtonClick() {
  isChangeNumberFieldVisible.value = !isChangeNumberFieldVisible.value;
}

async function onChangeNumberConfirmButtonClick() {
  if (!selectedPhoneNumber.value || !selectedPhoneDdi.value) {
    return;
  }

  isLoading.value = true;

  try {
    const res = await ApiService.patch('/bff/users/self-phone', {
      phone: selectedPhoneNumber.value,
      ddi: selectedPhoneDdi.value,
    });

    handleSuccess(res, async () => {
      await fetchUserProfile({ force: true });

      isChangeNumberFieldVisible.value = false;
      isChangedPhoneNumber.value = true;

      setPhoneToChange({
        phone: selectedPhoneNumber.value,
        ddi: selectedPhoneDdi.value,
      });

      handleSuccess(res, () => {
        openToaster({
          message: t('verification.sms.resend_successful'),
          type: 'success',
        });
      });

      setLastDateSmsSent(new Date().toISOString());
      startCountdown(); // Inicia o countdown após enviar o código
    });
  } catch (err) {
    handleError(err, 'auth.register.error.response_error');
  } finally {
    isLoading.value = false;
  }
}

const isChangedPhoneNumber = ref(false);

const disableChangeNumberButton = computed<boolean>(() => {
  return (
    (isChangedPhoneNumber.value && !canResendCode.value) || !canResendCode.value
  );
});

const phoneToShow = computed<string>(() => {
  if (isAuthenticated.value) {
    return phoneToChange.value
      ? phoneToChange.value.ddi + phoneToChange.value.phone
      : userPhoneDdi.value + userPhone.value;
  }

  return recoveryPasswordFlow.value?.final_phone || '';
});

const smsDescription = computed(() => {
  let description = t('verification.sms.desc');

  if (!isAuthenticated.value) {
    description += ` ${t('verification.sms.with_final')}`;
  }

  return description;
});

onMounted(async () => {
  await start();
});
</script>

<template>
  <div :class="$style.boxContainer">
    <BaseLoading
      class="!bg-bg-primary !absolute"
      :icon-loader="true"
      :show="isLoading"
    />

    <img
      alt="Email icon"
      aria-hidden="true"
      :class="$style.modalIcon"
      :src="getAssetsUrlForCDN('/assets/images/svg/sms.svg')"
    />

    <template v-if="isChangeNumberFieldVisible">
      <p :class="$style.title">
        {{ $t('verification.sms.change_number') }}
      </p>

      <p
        :class="[$style.description, 'max-w-full']"
        v-html="
          $t('verification.sms.change_desc', {
            current_number: phoneToShow,
          })
        "
      />

      <div :class="$style.changeNumberField">
        <BaseFormInputFullPhone
          id="phone"
          ref="inputPhoneRef"
          :initial-ddi="phoneToChange?.ddi"
          :initial-phone="phoneToChange?.phone"
          :required="true"
        />
      </div>

      <div :class="$style.buttonsContainer">
        <button
          :class="$style.backBtn"
          @click.prevent="onChangeNumberButtonClick"
        >
          {{ $t('general.cancel') }}
        </button>

        <button
          :class="[$style.submitBtn, 'btn-primary']"
          :disabled="!selectedPhoneNumber || !selectedPhoneDdi"
          @click="onChangeNumberConfirmButtonClick"
        >
          {{ $t('general.confirm') }}
          <NuxtIcon name="arrows/angle_right" />
        </button>
      </div>
    </template>

    <template v-else>
      <p :class="$style.title">
        {{ $t('verification.sms.title') }}
      </p>

      <p
        :class="$style.description"
        v-html="`${smsDescription}:`"
      />

      <span :class="$style.number">
        {{ phoneToShow }}
      </span>

      <BaseFormInputVerificationCode
        v-model="typedCode"
        @submit="onSubmit"
      />

      <p :class="$style.noCode">
        {{ $t('verification.sms.no_code') }}
      </p>

      <button
        :class="$style.resendCode"
        :disabled="!canResendCode"
        @click.prevent="onResendCodeButtonClick"
      >
        {{ $t('verification.resend_code') }}
        {{ canResendCode ? '' : `${$t('general.in')} ${secondsToResendCode}s` }}
      </button>

      <button
        v-if="isValidationModuleEnabled('global') && isAuthenticated"
        :class="$style.changeNumber"
        :disabled="disableChangeNumberButton"
        @click.prevent="onChangeNumberButtonClick"
      >
        {{ $t('verification.sms.change_number') }}
        {{
          disableChangeNumberButton
            ? `${$t('general.in')} ${secondsToResendCode}s`
            : ''
        }}
      </button>

      <div :class="$style.buttonsContainer">
        <button
          :class="[$style.submitBtn, 'btn-primary']"
          :disabled="!isValid"
          @click="onConfirmButtonClick"
        >
          {{ $t('verification.confirm_code') }}
          <NuxtIcon name="arrows/angle_right" />
        </button>
      </div>
    </template>
  </div>
</template>

<style module lang="scss">
.boxContainer {
  @apply p-3 md:p-6 flex flex-col justify-center flex-1 text-texts text-center;

  .modalIcon {
    @apply w-28 h-28 mx-auto mb-5;
  }

  .title {
    @apply font-bold mb-3;
  }

  .description {
    @apply mb-1 max-w-64 inline-block mx-auto text-sm text-texts/80;

    strong {
      @apply text-primary/80;
    }
  }

  .number {
    @apply text-sm mb-6 font-bold text-texts/70 pointer-events-none;
  }

  .changeNumberField {
    @apply w-full mt-4;
  }

  .noCode {
    @apply mt-5 py-1 font-medium text-sm;
  }

  .resendCode {
    @apply text-xs text-primary p-1 font-bold inline-flex mx-auto;

    &:disabled:not([disabled='false']) {
      @apply opacity-40 cursor-not-allowed;
    }
  }

  .changeNumber {
    @apply text-xs text-texts/50 p-1 font-bold inline-flex mx-auto;
  }

  .buttonsContainer {
    @apply flex items-center justify-center gap-2 mx-auto mt-5;

    button {
      @apply rounded-md gap-1.5 flex items-center font-bold uppercase h-10 focus:outline-none focus:ring-4;
    }

    .submitBtn {
      @apply py-5;
    }

    .backBtn {
      @apply bg-auth-texts/10 text-auth-texts/70 px-4 text-xs;
    }
  }
}
</style>
